import { router } from 'expo-router';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Platform, Pressable, ScrollView, Switch, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { SafeAreaView } from 'react-native-safe-area-context';

import { EventRow } from '@/components/event-row';
import { AppText } from '@/components/ui/app-text';
import { Card } from '@/components/ui/card';
import { ScreenHeader } from '@/components/ui/screen-header';
import { ensureNotificationSetup } from '@/notifications/local';
import { fixedCostRemindersOf, useFixedCostsStore } from '@/store/fixed-costs-store';
import { useRemindersStore } from '@/store/reminders-store';
import { colors, radius, spacing } from '@/theme/tokens';
import { daysUntil } from '@/utils/format';

/** Reminders list (stack screen, opened from Start / Więcej) */
export default function RemindersScreen() {
  const { t } = useTranslation();
  const { reminders, toggle } = useRemindersStore();
  const fixedCosts = useFixedCostsStore((s) => s.costs);
  const [pushGranted, setPushGranted] = useState<boolean | null>(null);

  useEffect(() => {
    // native only: ask for the local-notification permission on first open
    ensureNotificationSetup().then(setPushGranted).catch(() => setPushGranted(false));
  }, []);

  // Fixed costs join the calendar as virtual rows (next occurrence each) —
  // owner's ask: recurring payments visible among the deadlines.
  const sorted = [...reminders, ...fixedCostRemindersOf(fixedCosts)].sort(
    (a, b) => daysUntil(a.eventDate) - daysUntil(b.eventDate),
  );

  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: colors.background }} edges={['top', 'bottom']}>
      <ScrollView contentContainerStyle={{ padding: spacing.screen, gap: spacing.md, paddingBottom: spacing.xl }}>
        <ScreenHeader
          title={t('reminders.title')}
          rightIcon="add"
          onRightPress={() => router.push('/reminder-new')}
        />
        <AppText variant="bodyMd">{t('reminders.subtitle')}</AppText>

        {/* Agent messages: reminders is where the user looks for "what needs my
            attention", so the chat with the agent lives here too (owner: "jak
            agent napisze, niech się tu pojawi + guzik czatuj z agentem"). */}
        <Pressable accessibilityRole="button" onPress={() => router.push('/chat')}>
          <Card style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.sm, backgroundColor: colors.infoBg }}>
            <Ionicons name="chatbubble-ellipses-outline" size={24} color={colors.primary} />
            <View style={{ flex: 1 }}>
              <AppText variant="labelLg" color={colors.navy}>
                {t('reminders.agentChatTitle')}
              </AppText>
              <AppText variant="bodyMd">{t('reminders.agentChatSubtitle')}</AppText>
            </View>
            <Ionicons name="chevron-forward" size={18} color={colors.outlineVariant} />
          </Card>
        </Pressable>

        {/* Notification status */}
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            gap: spacing.sm,
            backgroundColor: colors.infoBg,
            borderRadius: radius.card,
            padding: spacing.md,
          }}
        >
          <Ionicons
            name={pushGranted ? 'notifications-outline' : 'notifications-off-outline'}
            size={22}
            color={colors.navy}
          />
          <AppText variant="bodyMd" style={{ flex: 1 }}>
            {Platform.OS === 'web'
              ? t('reminders.webInfo')
              : pushGranted
                ? t('reminders.pushOn')
                : t('reminders.pushOff')}
          </AppText>
        </View>

        <View style={{ gap: spacing.sm }}>
          {sorted.map((reminder) => (
            <EventRow
              key={reminder.id}
              reminder={reminder}
              trailing={
                reminder.type === 'FIXED_COST' ? (
                  // Virtual fixed-cost row — managed on the Opłaty stałe screen.
                  <Ionicons name="repeat-outline" size={20} color={colors.outlineVariant} />
                ) : (
                  <Switch
                    value={reminder.enabled}
                    onValueChange={() => toggle(reminder.id)}
                    trackColor={{ false: colors.disabledBg, true: colors.primary }}
                    thumbColor="#FFFFFF"
                  />
                )
              }
            />
          ))}
        </View>
      </ScrollView>
    </SafeAreaView>
  );
}
